/**
 * Reconciliation Log Table Component
 * Feature: 029-reconciliation-services (T059)
 * Constitution Version: 1.11.0
 *
 * Displays paginated drift logs with filters.
 * Spec 197 — migrated off MUI onto the token-driven kit.
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronUp } from 'lucide-react';
import {
  Select,
  Input,
  Badge,
  Alert,
  Spinner,
  DualScrollTable,
  PaginationControls,
} from '../ui';
import type { BadgeVariant } from '../ui';
import { reconciliationApi } from '../../services/reconciliationApi';
import type { ReconciliationLog } from '../../types/reconciliation.types';
import { formatEastern } from '@/utils/dateFormatter';

const SEVERITY_COLORS: Record<string, BadgeVariant> = {
  CRITICAL: 'error',
  HIGH: 'warning',
  MEDIUM: 'info',
  LOW: 'success',
};

const TH = 'px-3 py-2 text-left text-[13px] font-semibold text-[var(--color-text-primary)] whitespace-nowrap';
const TD = 'px-3 py-2 text-xs text-[var(--color-text-primary)] align-top';

function LogRow({ log }: { log: ReconciliationLog }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <tr className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-subtle)]">
        <td className={TD}>
          <button
            onClick={() => setOpen(!open)}
            aria-label={open ? 'Collapse' : 'Expand'}
            className="inline-flex items-center justify-center rounded-[var(--radius-md)] p-1 text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)] hover:text-[var(--color-text-primary)]"
          >
            {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </td>
        <td className={TD}>
          <Badge variant={SEVERITY_COLORS[log.severity]}>{log.severity}</Badge>
        </td>
        <td className={TD}>{log.drift_type}</td>
        <td className={TD}>{log.entity_type}</td>
        <td className={TD}>{log.entity_id.substring(0, 8)}...</td>
        <td className={TD}>{log.action_taken}</td>
        <td className={TD}>
          <Badge variant={log.success ? 'success' : 'error'}>{log.success ? 'Success' : 'Failed'}</Badge>
        </td>
        <td className={TD}>{formatEastern(log.created_at ?? log.logged_at)}</td>
      </tr>
      {open && (
        <tr className="border-b border-[var(--color-border)]">
          <td colSpan={8} className="px-4 py-3">
            <h4 className="mb-2 text-[length:var(--text-h3)] font-semibold text-[var(--color-text-primary)]">
              Drift Details
            </h4>
            <div className="flex flex-col gap-4 sm:flex-row">
              <div className="flex-1">
                <p className="mb-1 text-xs font-medium text-[var(--color-text-secondary)]">Old Value</p>
                <pre className="overflow-auto rounded-[var(--radius-md)] bg-[var(--color-bg-subtle)] p-3 text-xs text-[var(--color-text-primary)]">
                  {JSON.stringify(log.old_value, null, 2)}
                </pre>
              </div>
              <div className="flex-1">
                <p className="mb-1 text-xs font-medium text-[var(--color-text-secondary)]">New Value</p>
                <pre className="overflow-auto rounded-[var(--radius-md)] bg-[var(--color-bg-subtle)] p-3 text-xs text-[var(--color-text-primary)]">
                  {JSON.stringify(log.new_value, null, 2)}
                </pre>
              </div>
            </div>
            {log.error_details && (
              <div className="mt-3">
                <p className="mb-1 text-xs font-medium text-[var(--color-text-secondary)]">Error Details</p>
                <Alert variant="error">{log.error_details}</Alert>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

export default function ReconciliationLogTable() {
  const [page, setPage] = useState(0); // 0-based
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [serviceName, setServiceName] = useState<string>('');
  const [severity, setSeverity] = useState<string>('');
  const [entityType, setEntityType] = useState<string>('');
  const [entityId, setEntityId] = useState<string>('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['reconciliation', 'logs', page, rowsPerPage, serviceName, severity, entityType, entityId],
    queryFn: () =>
      reconciliationApi.getLogs({
        service_name: serviceName || undefined,
        severity: severity as any,
        entity_type: entityType as any,
        entity_id: entityId || undefined,
        page: page + 1,
        limit: rowsPerPage,
      }),
    refetchInterval: 30000,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return <Alert variant="error">Failed to load logs: {(error as Error).message}</Alert>;
  }

  const total = data?.total || 0;
  const logs = data?.logs || [];
  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));

  return (
    <div>
      {/* Filters */}
      <div className="mb-4 flex flex-wrap gap-3">
        <Select
          label="Service Name"
          selectSize="sm"
          value={serviceName}
          onChange={(e) => { setServiceName(e.target.value); setPage(0); }}
          options={[
            { value: '', label: 'All Services' },
            { value: 'AgentDeployment', label: 'Agent Deployment' },
            { value: 'WorkspaceMetadata', label: 'Workspace Metadata' },
            { value: 'ServicePrincipal', label: 'Service Principal' },
          ]}
        />
        <Select
          label="Severity"
          selectSize="sm"
          value={severity}
          onChange={(e) => { setSeverity(e.target.value); setPage(0); }}
          options={[
            { value: '', label: 'All' },
            { value: 'CRITICAL', label: 'Critical' },
            { value: 'HIGH', label: 'High' },
            { value: 'MEDIUM', label: 'Medium' },
            { value: 'LOW', label: 'Low' },
          ]}
        />
        <Select
          label="Entity Type"
          selectSize="sm"
          value={entityType}
          onChange={(e) => { setEntityType(e.target.value); setPage(0); }}
          options={[
            { value: '', label: 'All Types' },
            { value: 'workspace', label: 'Workspace' },
            { value: 'agent', label: 'Agent' },
            { value: 'service_principal', label: 'Service Principal' },
          ]}
        />
        <Input
          label="Entity ID"
          inputSize="sm"
          value={entityId}
          onChange={(e) => { setEntityId(e.target.value); setPage(0); }}
        />
      </div>

      {/* Table */}
      <DualScrollTable>
        <table className="w-full">
          <thead>
            <tr className="border-b-2 border-[var(--color-border)] bg-[var(--color-bg-subtle)]">
              <th className={TH} aria-label="Expand" />
              <th className={TH}>Severity</th>
              <th className={TH}>Drift Type</th>
              <th className={TH}>Entity Type</th>
              <th className={TH}>Entity ID</th>
              <th className={TH}>Action</th>
              <th className={TH}>Result</th>
              <th className={TH}>Timestamp</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log: ReconciliationLog) => (
              <LogRow key={log.id} log={log} />
            ))}
          </tbody>
        </table>
      </DualScrollTable>

      <PaginationControls
        page={page + 1}
        pageSize={rowsPerPage}
        total={total}
        totalPages={totalPages}
        start={page * rowsPerPage}
        shown={logs.length}
        onPage={(p) => setPage(p - 1)}
        onPageSize={(s) => { setRowsPerPage(s); setPage(0); }}
      />
    </div>
  );
}
